// Type-only, so nothing from the queue's component module is required at runtime. That
// matters: `VisitQueue` pulls in `EnhancedTable`, and a value import here would drag the
// whole table into every module that only wants to colour a state chip — which is enough
// to break the review screen's test suite outright.
import type { QcQueueState, QcQueueStateFilter } from './VisitQueue';

/**
 * How each state is coloured, and where its label lives.
 *
 * Here rather than in `VisitQueue` because two screens render this chip — the queue row
 * and the review screen's header — and a second copy of the mapping is how they end up
 * disagreeing about what "in review" looks like.
 *
 * The repo's default theme owns the colours. The handoff's navy/teal palette is read as
 * hierarchy intent, not as values to copy (docs/QUALITY_CHECK_COMPONENT_INVENTORY.md).
 */
export const STATE_PALETTE: Record<QcQueueState, 'info' | 'warning' | 'success'> = {
    submitted: 'info',
    in_review: 'warning',
    approved: 'success',
};

export const stateTranslateId = (state: QcQueueStateFilter): string =>
    `hospital.quality-check.queue.state.${state}`;

/** SKIN-239 — opaque visit identifiers the API ships so the client can label a row. */
export interface QcVisitCode {
    patientInvestigationUuid: string;
    patientIdInvestigation: number;
    visitUuid: string;
}

// Long enough that two visits of the same patient effectively never collide, short
// enough for a reviewer to read out loud. Visit.uuid is a v4 (Postgres
// uuid_generate_v4()), so these 8 hex chars are random, not sequential — the patient
// number in front is what keeps the code scannable and grouped.
const SHORT_VISIT_UUID_LENGTH = 8;

/**
 * The human-readable visit code, e.g. `P42-V1A2B3C4D`.
 *
 * The API is zero-knowledge — it never sees plaintext PII and therefore cannot build
 * a label — so it ships the opaque parts and the client assembles them here.
 */
export function buildVisitCode(code: QcVisitCode): string {
    const patient = `P${code.patientIdInvestigation}`;
    const visit = shortVisitCode(code.visitUuid);
    // A missing uuid can only come from a malformed response; still label the row by
    // patient rather than rendering "undefined" into the reviewer's queue.
    return visit ? `${patient}-${visit}` : patient;
}

/**
 * The visit half of the code — `V1A2B3C4D` — from the uuid alone.
 *
 * Split out of `buildVisitCode` so the queue and the review screen cannot drift. The
 * review screen has only the uuid to work with: the QC read (SKIN-256) returns no patient
 * identifier and the API is zero-knowledge, so the `P42-` prefix is not derivable there.
 * Rendering the same trailing segment from the same slice makes the shorter label a
 * prefix-consistent subset of the longer one rather than a second, competing name for the
 * same visit.
 */
export function shortVisitCode(visitUuid: string): string {
    const visit = (visitUuid || '')
        .replace(/-/g, '')
        .slice(0, SHORT_VISIT_UUID_LENGTH)
        .toUpperCase();
    return visit ? `V${visit}` : '';
}
